import { useEffect, useRef } from "react";
import { browserCookies, browserStorage, firstTouchQuery, hostOf, metaIdentifiers, pickAttribution, rememberedJourney, type VisitJourney } from "../../crmAttribution";

/**
 * Shared setup for the CRM's public pages (form, booking, manage booking): the tab
 * title, the light theme whatever the staff appearance setting says, and, inside
 * an embed, the page height posted to the parent so the iframe can fit it
 * (the snippet in the form builder listens for "nla-height").
 */
export function usePublicPage(title: string) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = document.documentElement;
    const previousTitle = document.title;
    const previousTheme = root.getAttribute("data-theme");
    document.title = title;
    root.setAttribute("data-theme", "light");
    root.classList.add("public-crm");
    // In an iframe the page sizes to its content (the parent sets the frame height).
    root.classList.toggle("embedded", isEmbedded());
    return () => {
      document.title = previousTitle;
      if (previousTheme) root.setAttribute("data-theme", previousTheme);
      else root.removeAttribute("data-theme");
      root.classList.remove("public-crm", "embedded");
    };
  }, [title]);
  useEffect(() => {
    if (window.parent === window || !ref.current) return;
    const el = ref.current;
    let last = 0;
    const post = () => {
      const h = Math.ceil(el.scrollHeight);
      if (h === last) return;
      last = h;
      window.parent.postMessage({ type: "nla-height", height: h }, "*");
    };
    const observer = new ResizeObserver(post);
    observer.observe(el);
    post();
    return () => observer.disconnect();
  });
  return ref;
}

/** The form builder's side preview: framed by NOLA itself, shown as the full page a lead sees. */
export const isLivePreview = () => {
  try { return window.self !== window.top && new URLSearchParams(window.location.search).get("live") === "1"; } catch { return false; }
};

/** True when the page is inside someone else's site (an iframe embed). */
export const isEmbedded = () => {
  if (isLivePreview()) return false;
  try { return window.self !== window.top; } catch { return true; }
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Where this lead came from, sent with a form's answers or a booking: UTM tags, ad
 * click ids, the page they first landed on and the site that sent them. In an
 * embed the page around it passes these in the address (a NOLA landing page or the
 * website snippet, which both remember an ad click from an earlier page or day);
 * document.referrer there is only that page. Opened on its own, the form or calendar
 * remembers the visit itself (rememberedVisit). A landing page built in NOLA also
 * passes its id (pg), so the lead counts for it.
 */
export function visitMeta() {
  const params = new URLSearchParams(window.location.search);
  const here = window.location.origin + window.location.pathname;
  const page = (params.get("page") || window.location.href).slice(0, 500);
  const pageId = params.get("pg") || "";
  if ((navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true || params.get("nla_consent") === "0") return { tracking_consent: false, page };
  let visit: VisitJourney;
  if (isEmbedded()) {
    const ref = params.get("referrer") || "";
    visit = { ...metaIdentifiers(pickAttribution(window.location.search)), landing: (params.get("landing") || "").slice(0, 500), referrer: hostOf(ref) && hostOf(ref) !== hostOf(page) ? ref.slice(0, 500) : "", first_touch: firstTouchQuery(params.get("first_touch")) };
  } else {
    visit = rememberedJourney({ search: window.location.search, here, referrer: document.referrer || "", storage: browserStorage(), cookie: browserCookies(), documentId: performance.timeOrigin });
  }
  const { landing = "", referrer = "", first_touch, last_touch: _last, ...utm } = visit;
  return {
    utm,
    landing: landing || (isEmbedded() ? "" : here.slice(0, 500)),
    referrer,
    page,
    ...(first_touch && Object.keys(first_touch).length ? { first_touch } : {}),
    ...(UUID.test(pageId) ? { page_id: pageId.toLowerCase() } : {}),
  };
}

/** "No Limits Marketing" with its mark, above public pages (not inside an embed: the host site has its own brand). */
export const BRAND_NAME = "No Limits Marketing";

/**
 * Tells the page framing this one that something happened, so a landing page
 * built in NOLA can move its funnel on: { type: "nla-form-done", qualified,
 * redirect, calendar } after a form is sent, { type: "nla-booked" } after a call
 * is booked. Only inside an embed, and only to a page on this same site (the
 * target origin is our own): another site embedding the form hears nothing.
 */
export function notifyParent(message: { type: string } & Record<string, unknown>) {
  if (!isEmbedded()) return;
  try { window.parent.postMessage(message, window.location.origin); } catch { /* the page still works without it */ }
}
